import axios from 'axios'
import { useAuthStore } from '../stores/authStore'

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:7046/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Only attempt token attachment when we truly have a user
      const authStore = useAuthStore()
      if (authStore.user) {
        const { accessToken, correlationId } = await authStore.getApiToken()
        config.headers.Authorization = `Bearer ${accessToken}`
        config.headers['X-Correlation-ID'] = correlationId
      }
    } catch (error) {
      console.error('Failed to get access token:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      console.warn('Unauthorized request - attempting token refresh')

      try {
        const authStore = useAuthStore()
        // If not authenticated, do not try to refresh; let guards handle redirect
        if (!authStore.user) {
          throw new Error('Not authenticated')
        }

        // Try to refresh the token when we have a user
        await authStore.refreshToken()

        // Retry the original request with new token
        if (authStore.user) {
          const { accessToken, correlationId } = await authStore.getApiToken()
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          originalRequest.headers['X-Correlation-ID'] = correlationId
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed or unauthenticated:', refreshError)
        // Do not force MSAL interaction here; allow route guard to redirect
        return Promise.reject(error)
      }
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

class ApiService {
  // Generic HTTP methods
  async get(endpoint, config = {}) {
    try {
      const response = await apiClient.get(endpoint, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await apiClient.post(endpoint, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async put(endpoint, data = {}, config = {}) {
    try {
      const response = await apiClient.put(endpoint, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async patch(endpoint, data = {}, config = {}) {
    try {
      const response = await apiClient.patch(endpoint, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async delete(endpoint, config = {}) {
    try {
      const response = await apiClient.delete(endpoint, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Error handler
  handleError(error) {
    const response = error.response;
    let errorMessage = error.message || 'An unexpected error occurred'

    let validationErrors = null

    // Detect network errors (no response from server)
    const isNetworkError = !response && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')

    // Detect timeout errors
    const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout')

    if (response?.data) {
      const responseData = response.data;

      if (responseData.errors && typeof responseData.errors === 'object') {
        validationErrors = responseData.errors

        const aggregated = []
        Object.values(responseData.errors).forEach((messages) => {
          if (Array.isArray(messages)) {
            messages.forEach(message => {
              aggregated.push(message)
            })
          }
        })

        errorMessage = aggregated.length > 0 ? aggregated.join(', ') : (responseData.title || errorMessage)
      } else {
        errorMessage = responseData.title || responseData.message || errorMessage;
      }
    }

    const statusCode = response?.status || null

    // Capture request details for debugging
    const requestUrl = error.config?.url || null
    const requestMethod = error.config?.method?.toUpperCase() || null
    const errorCode = error.code || null
    const correlationId = error.config?.headers?.['X-Correlation-ID'] || null

    return {
      message: errorMessage,
      statusCode,
      isNetworkError,
      isTimeoutError,
      validationErrors,
      originalError: error,
      // Debug info
      requestUrl,
      requestMethod,
      errorCode,
      correlationId,
    }
  }
}

export const apiService = new ApiService()
export { apiClient }
